import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { AcknowledgeModal, ActionModal, Flex, FormField, Modal, Text, TextInput } from '@percent/lemonade'
import { isValidWebsiteURL, useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { EditDonationProps } from './EditDonation.types'

type EditDonationFormValues = {
  campaignUrl: string
}

export function EditDonation({ donationId, externalPaymentUrl, open, onClose, onSuccess }: EditDonationProps) {
  const { t } = useTranslation()
  const { donationsService } = useServices()

  const [modalState, setModalState] = useState<'confirmation' | 'success' | 'error'>('confirmation')
  const validationSchema = useMemo(() => {
    return yup.object({
      campaignUrl: isValidWebsiteURL(t('errorMessage.validWebsite')).trim()
    })
  }, [t])

  const [{ isPending }, { apiFunc: editDonation }] = useMutation(
    donationsService.editDonation,
    () => {
      setModalState('success')
    },
    () => {
      setModalState('error')
    }
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<EditDonationFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      campaignUrl: externalPaymentUrl ?? ''
    }
  })

  useEffect(() => {
    if (!open) {
      return
    }

    reset({ campaignUrl: externalPaymentUrl ?? '' })
  }, [externalPaymentUrl, open, reset])

  const handleClose = useCallback(() => {
    setModalState('confirmation')
    onClose()
    reset({ campaignUrl: externalPaymentUrl ?? '' })
  }, [externalPaymentUrl, onClose, reset])

  const handleSuccessClose = useCallback(() => {
    setModalState('confirmation')
    onSuccess?.()
    onClose()
  }, [onClose, onSuccess])

  const handleFormSubmit = useCallback(
    (formValues: EditDonationFormValues) => {
      const trimmedCampaignUrl = formValues.campaignUrl.trim()
      const externalPaymentUrlPayload = trimmedCampaignUrl === '' ? null : trimmedCampaignUrl

      editDonation({ donationId, payload: { externalPaymentUrl: externalPaymentUrlPayload } })
    },
    [donationId, editDonation]
  )

  const modalContent = useMemo(() => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('dialog.editDonation.success.title')}
          buttonText={t('button.close')}
          handleClose={handleSuccessClose}
          viewTestId="edit-donation-success-modal"
        />
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('dialog.editDonation.error.title')}
          buttonText={t('button.close')}
          handleClose={handleClose}
          viewTestId="edit-donation-error-modal"
        />
      )
    }

    return (
      <ActionModal
        title={t('dialog.editDonation.title')}
        primaryButtonText={t('button.saveChanges')}
        secondaryButtonText={t('button.cancel')}
        disabled={!isValid || isPending}
        loading={isPending}
        loadingText={t('button.saving')}
        handleClose={handleClose}
        handleSubmit={handleSubmit(handleFormSubmit)}
        primaryBtnTestId="edit-donation-confirm-button"
        secondaryBtnTestId="edit-donation-cancel-button"
      >
        <Flex direction="column" gap="md">
          <Text size="small">{t('dialog.editDonation.descriptionPart')}</Text>
          <FormField
            label={t('dialog.editDonation.campaignUrl.label')}
            description={t('dialog.editDonation.warningTitle')}
            necessity="optional"
            status={errors.campaignUrl ? 'danger' : 'default'}
            statusMessage={errors.campaignUrl?.message}
          >
            <Controller
              name="campaignUrl"
              control={control}
              render={({ field: { name, onBlur, onChange, value } }) => (
                <TextInput
                  name={name}
                  placeholder={t('dialog.editDonation.campaignUrl.placeholder')}
                  value={value ?? ''}
                  onChange={event => {
                    onChange(event.target.value)
                  }}
                  onBlur={onBlur}
                />
              )}
            />
          </FormField>
        </Flex>
      </ActionModal>
    )
  }, [
    modalState,
    t,
    isPending,
    handleClose,
    handleSubmit,
    handleFormSubmit,
    errors.campaignUrl,
    isValid,
    control,
    handleSuccessClose
  ])

  return (
    <Modal open={open} onClose={handleClose}>
      {modalContent}
    </Modal>
  )
}
